import Handlebars from 'handlebars';
import { PUNCTUATION_MARKS_CONSTANTS } from '../constants/constants';
import { getNestedValue, isObjectRecord } from './handlebarsUtils';

/**
 * Handlebars helper to filter a data array based on specified criteria and collect unique values of a specified field.
 * @param dataArray - The array of data objects to filter.
 * @param filterOn - The field name in each object to filter on.
 * @param filterFor - The value(s) to filter for. Can be a string (comma-separated), an array, or a single value.
 * @param includeField - The field name whose unique values should be collected from matching objects.
 * @param separator - The separator to use when joining the collected values. Defaults to newline if not provided.
 * @returns A string of unique collected values joined by the specified separator.
 */
Handlebars.registerHelper(
    'filter',
    function (dataArray: unknown, filterOn: unknown, filterFor: unknown, includeField: unknown, separator: unknown) {
        const normalizedSeparator = typeof separator === 'string' ? separator : PUNCTUATION_MARKS_CONSTANTS.newLine;
        if (
            !Array.isArray(dataArray) ||
            typeof filterOn !== 'string' ||
            filterOn.length === 0 ||
            filterFor === undefined ||
            typeof includeField !== 'string' ||
            includeField.length === 0
        ) {
            return '';
        }

        const filterValues =
            typeof filterFor === 'string'
                ? filterFor
                      .split(',')
                      .map((v) => v.trim())
                      .filter(Boolean)
                : Array.isArray(filterFor)
                  ? filterFor
                  : [filterFor];
        const resultSet = new Set<unknown>();

        dataArray.forEach((item: unknown) => {
            const val = getNestedValue(item, filterOn);
            const includedValue = getNestedValue(item, includeField);

            if (includedValue === undefined || includedValue === null || includedValue === '') {
                return;
            }

            if (Array.isArray(val)) {
                if (val.some((v) => filterValues.includes(v))) {
                    resultSet.add(includedValue);
                }
            } else if (filterValues.includes(val)) {
                resultSet.add(includedValue);
            }
        });
        return Array.from(resultSet)
            .sort((a, b) => String(a).localeCompare(String(b)))
            .join(normalizedSeparator);
    }
);

/**
 * Handlebars helper to group a data array by a specified field and collect unique values of another field for each group.
 * @param dataArray - The array of data objects to group and collect from.
 * @param groupBy - The field name in each object to group by.
 * @param collectWhat - The field name whose unique values should be collected for each group.
 * @param groupSeparator - The separator to use when joining collected values within each group. Defaults to ' | ' if not provided.
 * @returns A string where each line represents a group and its collected values, formatted as "GroupValue: CollectedValues".
 */
Handlebars.registerHelper('groupAndCollect', function (dataArray, groupBy, collectWhat, groupSeparator) {
    if (typeof groupSeparator !== 'string') groupSeparator = ' ' + PUNCTUATION_MARKS_CONSTANTS.pipe + ' ';
    if (!Array.isArray(dataArray) || !groupBy || !collectWhat) return '';
    const groupMap: Record<string, Set<string>> = {};
    dataArray.forEach((item) => {
        let groupValuesRaw = item[String(groupBy)];
        let groupValues =
            groupValuesRaw === undefined || groupValuesRaw === null
                ? [undefined]
                : Array.isArray(groupValuesRaw)
                  ? groupValuesRaw
                  : [groupValuesRaw];
        groupValues.forEach((val) => {
            const bucket = val === undefined || val === null || val === '' ? 'Not Specified' : val;
            if (!groupMap[bucket]) groupMap[bucket] = new Set();
            const whatValues = Array.isArray(item[String(collectWhat)])
                ? item[String(collectWhat)]
                : [item[String(collectWhat)]];
            whatValues.forEach((wv) => {
                if (wv !== undefined && wv !== null && wv !== '') groupMap[bucket].add(wv);
            });
        });
    });
    return Object.entries(groupMap)
        .sort(([a], [b]) => String(a).localeCompare(String(b))) // Sort groups alphabetically
        .map(([groupValue, valuesSet]) => `${groupValue}: ${Array.from(valuesSet).join(groupSeparator)}`)
        .join('\n');
});

/**
 * Handlebars helper to join elements of an array into a string with a specified separator.
 * @param array - The array of elements to join.
 * @param separator - The separator to use between elements. If not provided, defaults to ', '.
 * @returns A string with the array elements joined by the specified separator, or an empty string if the input is not an array.
 */
Handlebars.registerHelper('join', function (array: unknown, separator: unknown) {
    if (!Array.isArray(array)) return '';
    const normalizedSeparator = typeof separator === 'string' ? separator : PUNCTUATION_MARKS_CONSTANTS.comma + ' ';
    return array.join(normalizedSeparator);
});

/**
 * Handlebars block helper to group array items by a field and render each group with the provided block template.
 * @param dataArray - Array of record-like objects to group.
 * @param groupBy - Object key used to determine the group bucket for each item.
 * @param options - Handlebars helper options containing the block renderer (`fn`).
 * @returns Concatenated rendered output for each group, or an empty string for invalid input.
 */
Handlebars.registerHelper(
    'groupBy',
    function (dataArray: unknown, groupBy: unknown, options: Handlebars.HelperOptions) {
        if (!Array.isArray(dataArray) || dataArray.length === 0 || typeof groupBy !== 'string' || groupBy.length === 0)
            return '';

        const groups: Record<string, Array<Record<string, unknown>>> = {};
        dataArray.forEach((item) => {
            if (!isObjectRecord(item)) return;
            const groupKey = String(item[groupBy] ?? 'Other');
            if (!groups[groupKey]) groups[groupKey] = [];
            groups[groupKey].push(item);
        });

        return Object.keys(groups)
            .map((groupKey) => {
                return options.fn({ key: groupKey, items: groups[groupKey] });
            })
            .join('');
    }
);

Handlebars.registerHelper('formatHiringTeam', function (hiringTeam) {
    const members = Array.isArray(hiringTeam)
        ? hiringTeam
        : isObjectRecord(hiringTeam) && Array.isArray(hiringTeam.members)
          ? hiringTeam.members
          : [];

    if (members.length === 0) return '';

    return members
        .map((member) => {
            if (!member || typeof member !== 'object') return '';

            const typedMember = member as {
                person?: { role?: unknown; name?: unknown };
                role?: unknown;
                name?: unknown;
            };

            const role = typedMember.person?.role ?? typedMember.role ?? '';
            const name = typedMember.person?.name ?? typedMember.name ?? '';

            if (!role && !name) return '';
            if (!role) return String(name);
            if (!name) return String(role);
            return `${role}: ${name}`;
        })
        .map((entry, index) => {
            if (entry) return entry;
            const member = members[index];
            if (!isObjectRecord(member)) return '';
            return typeof member.label === 'string' ? member.label.trim() : '';
        })
        .filter(Boolean)
        .join(PUNCTUATION_MARKS_CONSTANTS.newLine);
});

Handlebars.registerHelper('primaryLocationLabel', function (locations) {
    if (!Array.isArray(locations) || locations.length === 0) return PUNCTUATION_MARKS_CONSTANTS.dash;

    const typedLocations = locations as Array<{
        primary?: unknown;
        label?: unknown;
        address?: {
            locality?: unknown;
            adminArea?: unknown;
            countryRegion?: unknown;
        };
    }>;

    const primaryLocation = typedLocations.find((location) => Boolean(location?.primary)) ?? typedLocations[0];
    const explicitLabel = typeof primaryLocation?.label === 'string' ? primaryLocation.label.trim() : '';

    if (explicitLabel) {
        return explicitLabel;
    }

    const address = primaryLocation?.address;
    const fallbackLabel = [address?.locality, address?.adminArea, address?.countryRegion]
        .filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
        .map((value) => value.trim())
        .join(', ');

    return fallbackLabel || PUNCTUATION_MARKS_CONSTANTS.dash;
});

Handlebars.registerHelper('formatDateByLocale', function (dateString) {
    if (dateString === undefined || dateString === null || dateString === '') return '';

    const normalizedInput = String(dateString).trim();
    let parsedDate;

    // Check if the input is in 'YYYYMMDD' format
    if (/^\d{8}$/.test(normalizedInput)) {
        const year = Number(normalizedInput.slice(0, 4));
        const month = Number(normalizedInput.slice(4, 6));
        const day = Number(normalizedInput.slice(6, 8));

        parsedDate = new Date(year, month - 1, day);
        const isValidCalendarDate =
            parsedDate.getFullYear() === year && parsedDate.getMonth() === month - 1 && parsedDate.getDate() === day;

        if (!isValidCalendarDate) return normalizedInput;
    }
    // Parse ISO date-only values as local dates to avoid UTC day-shift in western timezones.
    else if (/^\d{4}-\d{2}-\d{2}$/.test(normalizedInput)) {
        const year = Number(normalizedInput.slice(0, 4));
        const month = Number(normalizedInput.slice(5, 7));
        const day = Number(normalizedInput.slice(8, 10));

        parsedDate = new Date(year, month - 1, day);
        const isValidCalendarDate =
            parsedDate.getFullYear() === year && parsedDate.getMonth() === month - 1 && parsedDate.getDate() === day;

        if (!isValidCalendarDate) return normalizedInput;
    }
    // Otherwise, attempt to parse it as a standard date (ISO 8601, timestamps, etc.)
    else if (/^\d+$/.test(normalizedInput)) {
        parsedDate = new Date(Number(normalizedInput));
    } else {
        parsedDate = new Date(normalizedInput);
    }

    // Validate the final parsed date object
    if (isNaN(parsedDate.getTime())) {
        return normalizedInput;
    }

    return new Intl.DateTimeFormat().format(parsedDate);
});
